import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FAMILIES = ["Vanilla / Sweet", "Floral", "Fresh / Clean", "Fruity", "Tropical", "Coconut", "Woody", "Warm / Cozy", "Citrus", "Masculine", "Other"];
const EMPTY = { name: "", email: "", phone: "", candle_size: "9 oz", scent_family: "", fragrance_notes: "", quantity: "1", occasion: "", additional: "" };

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.28em] text-stone2">{label}</span>
    {children}
  </label>
);

const CreateScent = () => {
  const [form, setForm] = useState(EMPTY);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { type: "custom_scent", data: form });
      setSent(true);
      toast.success("Your scent request has been received");
    } catch {
      toast.error("Something went wrong — please try again");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="create-scent-page" className="bg-ivory">
      <section className="border-b border-line py-16 md:py-24 text-center">
        <Reveal>
          <p className="eyebrow">Create Your Own Scent</p>
          <h1 className="mx-auto mt-5 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">
            Your Candle. Your Fragrance. <em>Your Story.</em>
          </h1>
          <p className="mx-auto mt-8 max-w-xl text-sm md:text-base leading-relaxed text-stone2">
            Have a fragrance in mind? Tell us what you love and let Merari Candle Company help create a soy wax candle that's uniquely yours.
          </p>
        </Reveal>
      </section>
      <Marquee />
      <section className="mx-auto max-w-3xl px-6 sm:px-12 py-16 md:py-24">
        {sent ? (
          <Reveal className="border border-line bg-white p-12 text-center" data-testid="scent-success">
            <p className="font-serif text-3xl font-light italic">Your story is in good hands.</p>
            <p className="mt-4 text-sm text-stone2 leading-relaxed">We've received your custom scent request and will reach out within 2-3 business days to begin crafting your candle.</p>
            <button onClick={() => { setSent(false); setForm(EMPTY); }} data-testid="scent-create-another" className="btn-outline mt-8">Create Another</button>
          </Reveal>
        ) : (
          <Reveal>
            <form onSubmit={submit} data-testid="custom-scent-form" className="border border-line bg-white p-8 md:p-12 grid gap-6 sm:grid-cols-2">
              <Field label="Name" testid="field-scent-name"><input required data-testid="scent-name-input" value={form.name} onChange={set("name")} placeholder="Your full name" /></Field>
              <Field label="Email" testid="field-scent-email"><input required type="email" data-testid="scent-email-input" value={form.email} onChange={set("email")} placeholder="you@email.com" /></Field>
              <Field label="Phone Number" testid="field-scent-phone"><input data-testid="scent-phone-input" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" /></Field>
              <Field label="Candle Size" testid="field-scent-size">
                <select data-testid="scent-size-select" value={form.candle_size} onChange={set("candle_size")}>
                  <option>9 oz</option><option>4 oz</option><option>Multiple Sizes</option><option>Not Sure Yet</option>
                </select>
              </Field>
              <Field label="Preferred Scent Family" testid="field-scent-family">
                <select required data-testid="scent-family-select" value={form.scent_family} onChange={set("scent_family")}>
                  <option value="">Select a scent family…</option>
                  {FAMILIES.map((f) => <option key={f}>{f}</option>)}
                </select>
              </Field>
              <Field label="Quantity" testid="field-scent-quantity"><input type="number" min="1" data-testid="scent-quantity-input" value={form.quantity} onChange={set("quantity")} /></Field>
              <div className="sm:col-span-2"><Field label="Desired Fragrance Notes" testid="field-scent-notes"><textarea rows="3" required data-testid="scent-notes-input" value={form.fragrance_notes} onChange={set("fragrance_notes")} placeholder="e.g. warm vanilla with a hint of sandalwood and fig…" /></Field></div>
              <div className="sm:col-span-2"><Field label="Occasion" testid="field-scent-occasion"><input data-testid="scent-occasion-input" value={form.occasion} onChange={set("occasion")} placeholder="Wedding, gift, everyday — optional" /></Field></div>
              <div className="sm:col-span-2"><Field label="Additional Requests" testid="field-scent-additional"><textarea rows="3" data-testid="scent-additional-input" value={form.additional} onChange={set("additional")} placeholder="Anything else we should know?" /></Field></div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={loading} data-testid="custom-scent-submit" className="btn-primary w-full disabled:opacity-50">
                  {loading ? "Sending…" : "Create My Scent"}
                </button>
              </div>
            </form>
          </Reveal>
        )}
      </section>
    </div>
  );
};

export default CreateScent;
